import CustomerService from '@/components/CustomerService.vue'
import { useUserStore } from '@/store'
import { createComponent } from '@/utils/createComponent'
import { http } from '@/utils/http'

// 商城相关接口
/**
 * 获取商城商品列表
 * @param params 查询参数
 */
export const apiMallProductList = (params: PaginationParams) =>
  http<BaseResponse<MallProductResource[]>>({
    url: '/api/mall/product',
    method: 'GET',
    params,
  })

/**
 * 购买商城商品
 * @param data 购买信息
 */
export const apiMallProductBuy = (data: BuyMallProductRequest) =>
  http<BaseResponse>({ url: '/api/mall/buy', method: 'POST', data })

/**
 * 获取token记录
 * @param params 查询参数
 */
export const apiMallTokenList = (params: PaginationParams) =>
  http<BaseResponse<any[]>>({
    url: '/api/mall/token',
    method: 'GET',
    params,
  })

/**
 * 获取商城订单列表
 * @param params 查询参数
 */
export const apiMallOrderList = (params: PaginationParams) =>
  http<BaseResponse<MallOrderResource[]>>({
    url: '/api/mall/order',
    method: 'GET',
    params,
  })

/**
 * 订单签收
 * @param data 签收信息
 */
export const apiMallOrderSignFor = (data: MallOrderSignForRequest) =>
  http<BaseResponse>({ url: '/api/mall/signfor', method: 'POST', data })

// 用户相关接口
/**
 * 用户注册
 * @param data 注册信息
 */
export const apiUserRegister = (data: UserRegisterRequest) =>
  http<BaseResponse>({ url: '/api/user/register', method: 'POST', data })

/**
 * 用户登录
 * @param data 登录信息
 */
export const apiUserLogin = (data: UserLoginRequest) =>
  http<BaseResponse<{ token: string }>>({ url: '/api/user/login', method: 'POST', data })

/**
 * 获取用户信息
 */
export const apiUserInfo = () =>
  http<BaseResponse<UserInfoResponse>>({ url: '/api/user/info', method: 'GET' })

/**
 * 用户退出登录
 */
export const apiUserLogout = () => http<BaseResponse>({ url: '/api/user/logout', method: 'POST' })

/**
 * 用户实名认证
 * @param data 实名认证信息
 */
export const apiUserAuthenticate = (data: UserAuthenticateRequest) =>
  http<BaseResponse>({ url: '/api/user/authenticate', method: 'POST', data })

/**
 * 修改登录密码
 * @param data 密码信息
 */
export const apiUserChangePassword = (data: ModifyPasswordRequest) =>
  http<BaseResponse>({ url: '/api/user/password', method: 'POST', data })

/**
 * 修改提现密码
 * @param data 密码信息
 */
export const apiUserChangeWithdrawPassword = (data: ModifyPasswordRequest) =>
  http<BaseResponse>({ url: '/api/user/withdraw_password', method: 'POST', data })

/**
 * 获取资金明细
 * @param params 查询参数
 */
export const apiBalanceStatement = (
  params: PaginationParams & {
    // 1=投资委托产品，2=委托产品收益，3=团队奖励（个人），4=团队奖励（团队），5=钱包充值，6=银行卡充值，7=钱包提现，8=银行卡提现，9=委托产品提取，10=购买数字藏品，11=卖出数字藏品，12=激活股权，13=存入复利宝，14=复利宝提取，15=推荐奖励，16=划转转出，17=划转转入，18=复利宝收益
    type?: number
    begin_date?: string
    end_date?: string
  },
) =>
  http<BaseResponse<any[]>>({
    url: '/api/user/balance',
    method: 'GET',
    params,
  })

// 验证码相关接口
/**
 * 获取验证码
 */
export const apiCaptcha = () =>
  http<BaseResponse<CaptchaResource>>({ url: '/api/captcha', method: 'GET' })

// 首页相关接口
/**
 * 获取首页数据
 */
export const apiHome = () =>
  http<BaseResponse<HomeResource>>({
    url: '/api/home',
    method: 'GET',
  })

// 公告资讯相关接口
/**
 * 获取公告列表
 * @param params 查询参数
 */
export const apiNoticeList = (params: PaginationParams) =>
  http<BaseResponse<NoticeItem[]>>({
    url: '/api/notice',
    method: 'GET',
    params,
  })

/**
 * 获取公告详情
 * @param id 公告ID
 */
export const apiNoticeDetail = (id: string) =>
  http<BaseResponse<NoticeItem>>({ url: `/api/notice/${id}`, method: 'GET' })

/**
 * 获取文章列表
 * @param params 查询参数
 */
export const apiArticleList = (params: PaginationParams & { type: '1' | '2' }) =>
  http<BaseResponse<ArticleResource[]>>({
    url: '/api/article',
    method: 'GET',
    params,
  })

/**
 * 获取文章详情
 * @param id 文章ID
 */
export const apiArticleDetail = (id: string) =>
  http<BaseResponse<ArticleResource>>({ url: `/api/article/${id}`, method: 'GET' })

/**
 * 获取媒体信息列表
 * @param params 查询参数
 */
export const apiMediaList = (params: PaginationParams) =>
  http<BaseResponse<MediaInfoResource[]>>({
    url: '/api/media',
    method: 'GET',
    params,
  })

/**
 * 获取关于我们
 */
export const apiAboutUs = () =>
  http<BaseResponse<{ content: string | null }>>({ url: '/api/aboutus', method: 'GET' })

/**
 * 获取新手引导
 */
export const apiGuide = () =>
  http<BaseResponse<{ content: string | null }>>({ url: '/api/guide', method: 'GET' })

// 团队介绍相关接口
/**
 * 获取经纪人类目
 * @param params 查询参数
 */
export const apiBrokerCategory = (params: PaginationParams) =>
  http<BrokerCategory[]>({
    url: '/api/broker/category',
    method: 'GET',
    params,
  })

/**
 * 获取经纪人列表
 * @param params 查询参数
 */
export const apiBrokerList = (params: PaginationParams & { category_id?: number }) =>
  http<BaseResponse<BrokerItem[]>>({
    url: '/api/broker',
    method: 'GET',
    params,
  })

/**
 * 获取经纪人详情
 * @param id 经纪人ID
 */
export const apiBrokerDetail = (id: string) =>
  http<BaseResponse<BrokerItem>>({ url: `/api/broker/${id}`, method: 'GET' })

// 收款账号相关接口
/**
 * 获取银行卡列表
 */
export const apiBankCardList = () =>
  http<BaseResponse<BankCardItem[]>>({ url: '/api/payment/account/card', method: 'GET' })

/**
 * 添加银行卡
 * @param data 银行卡信息
 */
export const apiBankCardAdd = (data: AddBankCardRequest) =>
  http<BaseResponse>({ url: '/api/payment/account/card', method: 'POST', data })

/**
 * 编辑银行卡
 * @param id 银行卡ID
 * @param data 银行卡信息
 */
export const apiBankCardEdit = (id: string, data: AddBankCardRequest) =>
  http<BaseResponse>({ url: `/api/payment/account/card/${id}`, method: 'PUT', data })

/**
 * 删除银行卡
 * @param id 银行卡ID
 */
export const apiBankCardDelete = (id: string) =>
  http<BaseResponse>({ url: `/api/payment/account/card/${id}`, method: 'DELETE' })

/**
 * 获取钱包列表
 */
export const apiWalletList = () =>
  http<BaseResponse<WalletItem[]>>({ url: '/api/payment/account/wallet', method: 'GET' })

/**
 * 添加钱包
 * @param data 钱包信息
 */
export const apiWalletAdd = (data: AddWalletRequest) =>
  http<BaseResponse>({ url: '/api/payment/account/wallet', method: 'POST', data })

/**
 * 编辑钱包
 * @param id 钱包ID
 * @param data 钱包信息
 */
export const apiWalletEdit = (id: string, data: AddWalletRequest) =>
  http<BaseResponse>({ url: `/api/payment/account/wallet/${id}`, method: 'PUT', data })

/**
 * 删除钱包
 * @param id 钱包ID
 */
export const apiWalletDelete = (id: string) =>
  http<BaseResponse>({ url: `/api/payment/account/wallet/${id}`, method: 'DELETE' })

// 行情/产品相关接口
/**
 * 获取产品类目
 */
export const apiProductCategory = () =>
  http<BaseResponse<ProductCategory[]>>({
    url: '/api/investment/product/category',
    method: 'GET',
  })

/**
 * 获取产品列表
 * @param params 查询参数
 */
export const apiProductList = (params: PaginationParams & { category_id?: number }) =>
  http<BaseResponse<InvestmentProductResource[]>>({
    url: '/api/investment/product',
    method: 'GET',
    params,
  })

/**
 * 获取产品详情
 * @param id 产品ID
 */
export const apiProductDetail = (id: string) =>
  http<BaseResponse<InvestmentProductResource>>({
    url: `/api/investment/product/${id}`,
    method: 'GET',
  })

/**
 * 购买产品
 * @param data 购买信息
 */
export const apiProductBuy = (data: ProductBuyRequest) =>
  http<BaseResponse>({ url: '/api/investment/product', method: 'POST', data })

// 收货信息相关接口
/**
 * 获取收货信息列表
 */
export const apiDeliveryInfoList = () =>
  http<BaseResponse<{ data: DeliveryInfoItem[] }>>({ url: '/api/delivery_info', method: 'GET' })

/**
 * 添加收货信息
 * @param data 收货信息
 */
export const apiDeliveryInfoAdd = (data: AddDeliveryInfoRequest) =>
  http<BaseResponse>({ url: '/api/delivery_info', method: 'POST', data })

/**
 * 编辑收货信息
 * @param id 收货信息ID
 * @param data 收货信息
 */
export const apiDeliveryInfoEdit = (id: string, data: AddDeliveryInfoRequest) =>
  http<BaseResponse>({ url: `/api/delivery_info/${id}`, method: 'PUT', data })

/**
 * 删除收货信息
 * @param id 收货信息ID
 */
export const apiDeliveryInfoDelete = (id: string) =>
  http<BaseResponse>({ url: `/api/delivery_info/${id}`, method: 'DELETE' })

// 订单相关接口
/**
 * 用户充值
 * @param data 充值信息
 */
export const apiOrderRecharge = (data: any) =>
  http({ url: '/api/order/recharge', method: 'POST', data })

/**
 * 用户提现
 * @param data 提现信息
 */
export const apiOrderWithdraw = (data: any) =>
  http({ url: '/api/order/withdraw', method: 'POST', data })

/**
 * 预划转
 */
export const apiOrderTransferPrepare = () =>
  http<BaseResponse<{ min_amount: string; min_quantity: string }>>({
    url: '/api/order/transfer/prepare',
    method: 'GET',
  })

/**
 * 用户划转
 * @param data 划转信息
 */
export const apiOrderTransfer = (data: any) =>
  http({ url: '/api/order/transfer', method: 'POST', data })

/**
 * 获取投资订单列表
 * @param params 查询参数
 */
export const apiInvestmentOrderList = (params: PaginationParams & { state?: '0' | '1' }) =>
  http<BaseResponse<any[]>>({
    url: '/api/order/investment',
    method: 'GET',
    params,
  })

// NFT相关接口
/**
 * 获取NFT交易市场列表
 * @param params 查询参数
 */
export const apiNftList = (params: PaginationParams) =>
  http<BaseResponse<NftCardResource[]>>({
    url: '/api/nft',
    method: 'GET',
    params,
  })

/**
 * 获取已持有NFT列表
 */
export const apiNftMyList = () =>
  http<BaseResponse<{ data: MyNftCardResource[] }>>({
    url: '/api/nft/my',
    method: 'GET',
  })

/**
 * 获取挂卖中NFT列表
 */
export const apiNftSellingList = () =>
  http<BaseResponse<{ data: MyNftCardResource[] }>>({
    url: '/api/nft/selling',
    method: 'GET',
  })

/**
 * 获取NFT成交记录
 * @param params 查询参数
 */
export const apiNftTransactionList = (params: PaginationParams) =>
  http<BaseResponse<NftCardTransactionResource[]>>({
    url: '/api/nft/transaction',
    method: 'GET',
    params,
  })

/**
 * NFT挂卖
 * @param data 挂卖信息
 */
export const apiNftSell = (data: NftCardSellRequest) =>
  http<BaseResponse>({ url: '/api/nft/sell', method: 'POST', data })

/**
 * NFT买入
 * @param data 买入信息
 */
export const apiNftBuy = (data: NftCardBuyRequest) =>
  http<BaseResponse>({ url: '/api/nft/buy', method: 'POST', data })

// 复利宝相关接口
/**
 * 获取复利宝参数
 */
export const apiFlbInfo = () =>
  http<BaseResponse<FlbResource>>({
    url: '/api/flb',
    method: 'GET',
  })

/**
 * 复利宝存入
 * @param data 存入信息
 */
export const apiFlbDeposit = (data: FlbRequest) =>
  http<BaseResponse>({ url: '/api/flb/deposit', method: 'POST', data })

/**
 * 复利宝提取
 * @param data 提取信息
 */
export const apiFlbWithdraw = (data: FlbRequest) =>
  http<BaseResponse>({ url: '/api/flb/withdraw', method: 'POST', data })

// 股权相关接口
/**
 * 股权参数
 */
export const apiShareholdingInfo = (params: PaginationParams) =>
  http<
    BaseResponse<{
      // 激活费用(CNY)
      amount: number
      // 股权介绍
      description: string
      // 兑换DNCA比例(1:X)
      exchange_ratio: string
    }>
  >({
    url: '/api/shareholding',
    method: 'GET',
    params,
  })

/**
 * 获取股权记录
 * @param params 查询参数
 */
export const apiShareholdingList = (params: PaginationParams) =>
  http<BaseResponse<ShareholdingStatementResource[]>>({
    url: '/api/shareholding/statement',
    method: 'GET',
    params,
  })

/**
 * 激活股权
 */
export const apiShareholdingActive = () =>
  http<BaseResponse>({ url: '/api/shareholding/active', method: 'POST' })

/**
 * 兑换股权
 * @param data 兑换信息
 */
export const apiShareholdingExchange = (data: { quantity: number }) =>
  http<BaseResponse>({ url: '/api/shareholding/exchange', method: 'POST', data })

/**
 * DNCA提现
 */
export const apiDncaWithdraw = (data: { quantity: number; address: string }) =>
  http<BaseResponse>({ url: '/api/order/withdraw/dnca', method: 'POST', data })

// Web3相关接口
/**
 * 获取web3信息
 */
export const apiWeb3Info = () =>
  http<BaseResponse<Web3Resource>>({
    url: '/api/web3',
    method: 'GET',
  })

// 充值记录相关接口
/**
 * 获取充值记录
 * @param params 查询参数
 */
export const apiRechargeList = (params: PaginationParams) =>
  http<BaseResponse<RechargeOrderResource[]>>({
    url: '/api/order/recharge',
    method: 'GET',
    params,
  })

// 提现记录相关接口
/**
 * 获取提现记录
 * @param params 查询参数
 */
export const apiWithdrawList = (params: PaginationParams) =>
  http<BaseResponse<WithdrawOrderResource[]>>({
    url: '/api/order/withdraw',
    method: 'GET',
    params,
  })

// 划转记录相关接口
/**
 * 获取划转记录
 * @param params 查询参数
 */
export const apiTransferList = (params: PaginationParams) =>
  http<BaseResponse<TransferOrderResource[]>>({
    url: '/api/order/transfer',
    method: 'GET',
    params,
  })

// 实名认证相关接口
/**
 * 获取实名认证信息
 */
export const apiUserAuthInfo = () =>
  http<BaseResponse<UserAuthResource>>({
    url: '/api/user/authentication',
    method: 'GET',
  })

// 团队奖励接口
/**
 * 个人/团队信息
 * @param params 查询参数
 * type: 1=个人，2=团队
 */
export const apiTeamInfo = (params: PaginationParams & { type: '1' | '2' }) =>
  http<
    BaseResponse<{
      mobile: string
      rules: []
      tag_name: string | null
      total_investment_amount: string
      total_invite_count: number
      total_reward_amount: string
    }>
  >({
    url: '/api/team',
    method: 'GET',
    params,
  })

// 下级成员
/**
 * 获取下级成员列表
 * @param params 查询参数
 * type: 1=个人，2=团队
 */
export const apiTeamMemberList = (params: PaginationParams & { type: '1' | '2' }) =>
  http<
    BaseResponse<{
      mobile: string
      total_investment_amount: string
      total_invite_count: number
      total_reward_amount: string
    }>
  >({
    url: '/api/team/member',
    method: 'GET',
    params,
  })

// 行情类目
/**
 * 获取行情类目
 */
export const apiQuoteCategory = () =>
  http<BaseResponse<Array<{ id: number; name: string }>>>({
    url: '/api/quote/category',
    method: 'GET',
  })

// 行情产品
/**
 * 获取行情产品列表
 * @param params 查询参数
 */
export const apiQuoteProductList = (params: { category_id: number }) =>
  http<
    BaseResponse<
      Array<{
        id: number
        code: string
        name: string
        price: string
        incr_ratio: string
        rf_price: string
      }>
    >
  >({
    url: '/api/quote/product',
    method: 'GET',
    params,
  })

// DNCA相关
export const apiDncaExchangePrepare = () =>
  http({ url: '/api/user/balance/exchange/prepare', method: 'GET' })
export const apiDncaExchange = (data: any) =>
  http({ url: '/api/user/balance/exchange', method: 'POST', data })

// 下载相关接口
/**
 * 下载
 */
export const apiDownloadPageConfig = () =>
  http<
    BaseResponse<{
      app_download: { android: string; ios: string }
      customer_service: Array<{ link: string; is_online: 0 | 1 }>
    }>
  >({
    url: '/api/download',
    method: 'GET',
  })

// 在线客服
export const getOnlineCustomerServiceAPI = async () => {
  const { vnode, destroy } = createComponent(
    CustomerService,
    {
      onClosed: () => {
        // 关闭时销毁组件
        destroy()
      },
    },
    getCurrentInstance()?.appContext,
  )
  vnode.component?.exposed?.open()
}

// 支付列表
export const apiPayList = (params) =>
  // type: 1=在线支付，2=支付宝支付, state: 0=未支付，1=支付中，2=支付成功，3=支付失败
  http<BaseResponse<{ type: 1 | 2; amount: string; state: 0 | 1 | 2; created_at: string }[]>>({
    url: '/api/pay',
    method: 'GET',
    params,
  })

// 发起支付
export const apiPay = (data: { pay_type: 'quick' | 'code' | 'wechat'; amount: number }) =>
  http<BaseResponse<{ pay_url: string }>>({
    url: '/api/pay/confirm',
    method: 'POST',
    data,
  })
